import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Link2, RefreshCw, ShieldCheck, Unplug, UploadCloud } from 'lucide-react';
import axiosInstance from '../services/axiosInstance';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BrokerIntegrations = () => {
  const [providers, setProviders] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [provider, setProvider] = useState('');
  const [tokenProvider, setTokenProvider] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [accountId, setAccountId] = useState('');
  const [oauthClientId, setOauthClientId] = useState('');
  const [oauthClientSecret, setOauthClientSecret] = useState('');
  const [customProviderName, setCustomProviderName] = useState('');
  const [customTradesUrl, setCustomTradesUrl] = useState('');
  const [customHeadersJson, setCustomHeadersJson] = useState('');
  const [customMappingJson, setCustomMappingJson] = useState('');

  const [fromDate, setFromDate] = useState(getLastNDaysDate(30));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));

  const oauthProviders = useMemo(
    () => providers.filter((p) => (p.auth_type || 'oauth2') === 'oauth2'),
    [providers]
  );
  const selectedOauthProvider = useMemo(
    () => providers.find((p) => p.id === provider) || null,
    [providers, provider]
  );
  const backendBase = (axiosInstance.defaults.baseURL || '').replace(/\/$/, '');

  function getLastNDaysDate(days) {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  }

  const fetchProvidersAndConnections = async () => {
    setLoading(true);
    setError('');
    try {
      const [providerRes, connectionRes] = await Promise.all([
        axiosInstance.get('/broker/providers'),
        axiosInstance.get('/broker/connections')
      ]);
      const providerList = providerRes.data?.providers || [];
      setProviders(providerList);
      setConnections(connectionRes.data?.connections || []);

      const oauthProviderList = providerList.filter((p) => (p.auth_type || 'oauth2') === 'oauth2');
      const firstEnabledOauth = oauthProviderList.find((p) => p.enabled) || oauthProviderList[0];
      if (!provider) {
        setProvider(firstEnabledOauth?.id || '');
      }
      if (!tokenProvider && providerList.length > 0) {
        setTokenProvider(providerList[0].id);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load broker data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProvidersAndConnections();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('broker_connect');
    const providerId = params.get('provider');
    const reason = params.get('reason');
    if (!status) return;

    if (status === 'success') {
      setMessage(`Broker connected successfully: ${providerId || ''}`);
      setError('');
      fetchProvidersAndConnections();
    } else {
      setError(`Broker connection failed: ${reason || 'Unknown reason'}`);
      setMessage('');
    }

    params.delete('broker_connect');
    params.delete('provider');
    params.delete('reason');
    const query = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }, []);

  const openOAuthConnect = async () => {
    if (!provider) return;
    setError('');
    setMessage('');
    try {
      if (!backendBase) {
        throw new Error('Backend URL is not configured');
      }

      const redirectUri = `${backendBase}/broker/connect/callback/${provider}`;
      const frontendRedirectUri = `${window.location.origin}/broker-integrations`;
      const res = await axiosInstance.post('/broker/connect/start', {
        provider,
        redirect_uri: redirectUri,
        frontend_redirect_uri: frontendRedirectUri,
        oauth_client_id: oauthClientId || undefined,
        oauth_client_secret: oauthClientSecret || undefined
      });
      const authUrl = res.data?.auth_url;
      if (!authUrl) throw new Error('Broker auth URL not received');

      const popup = window.open(authUrl, '_blank', 'width=560,height=760');
      if (!popup) throw new Error('Popup blocked. Please allow popups and retry.');

      const poll = setInterval(() => {
        if (popup.closed) {
          clearInterval(poll);
          fetchProvidersAndConnections();
        }
      }, 1500);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to start broker connection');
    }
  };

  const connectToken = async () => {
    if (!tokenProvider || !accessToken) {
      setError('Provider and access token are required');
      return;
    }
    setError('');
    setMessage('');
    try {
      let customHeaders;
      let customMapping;
      if (tokenProvider === 'custom') {
        if (!customProviderName.trim()) {
          throw new Error('Custom broker name is required');
        }
        if (customHeadersJson.trim()) {
          customHeaders = JSON.parse(customHeadersJson);
        }
        if (customMappingJson.trim()) {
          customMapping = JSON.parse(customMappingJson);
        }
      }

      await axiosInstance.post('/broker/connect/token', {
        provider: tokenProvider,
        access_token: accessToken,
        refresh_token: refreshToken,
        account_id: accountId,
        provider_name: tokenProvider === 'custom' ? customProviderName : undefined,
        custom_trades_url: tokenProvider === 'custom' ? customTradesUrl : undefined,
        custom_headers: tokenProvider === 'custom' ? customHeaders : undefined,
        custom_mapping: tokenProvider === 'custom' ? customMapping : undefined
      });
      setMessage('Broker connected using token successfully');
      setAccessToken('');
      setRefreshToken('');
      setAccountId('');
      setCustomProviderName('');
      setCustomTradesUrl('');
      setCustomHeadersJson('');
      setCustomMappingJson('');
      fetchProvidersAndConnections();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to connect broker token');
    }
  };

  const syncConnection = async (connectionId) => {
    setSyncingId(connectionId);
    setError('');
    setMessage('');
    try {
      const res = await axiosInstance.post(`/broker/sync/${connectionId}`, {
        from_date: fromDate,
        to_date: toDate
      });
      const summary = res.data?.result;
      setMessage(
        `Sync completed: ${summary?.normalized || 0} normalized, ${summary?.new_trades || 0} new trades`
      );
      fetchProvidersAndConnections();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Sync failed');
    } finally {
      setSyncingId('');
    }
  };

  const disconnectConnection = async (connectionId) => {
    setError('');
    setMessage('');
    try {
      await axiosInstance.post(`/broker/disconnect/${connectionId}`);
      setMessage('Broker disconnected');
      fetchProvidersAndConnections();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Disconnect failed');
    }
  };

  return (
    <div className="p-8 space-y-6">
      <Card className="border-0 shadow-xl bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-2xl">
            <ShieldCheck className="w-7 h-7 text-emerald-300" />
            Broker Integrations
          </CardTitle>
          <p className="text-slate-200">
            Securely connect broker accounts, sync trades, and auto-save buy/sell entries into your portfolio.
          </p>
        </CardHeader>
      </Card>

      {message && (
        <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-emerald-700 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          {message}
        </div>
      )}
      {error && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-3 text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="shadow-lg border-0 ring-1 ring-blue-200/50">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-600" />
              OAuth Connect
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="mb-2 block">Broker Provider</Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger className="bg-white">
                <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {oauthProviders.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} {p.enabled ? '' : '(disabled)'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedOauthProvider && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 space-y-2">
                <p>
                  <span className="font-semibold">Setup:</span> {selectedOauthProvider.help_text || 'Configure OAuth credentials in backend env.'}
                </p>
                {!!selectedOauthProvider.required_env?.length && (
                  <p>
                    <span className="font-semibold">Required env:</span> {selectedOauthProvider.required_env.join(', ')}
                  </p>
                )}
                {!!selectedOauthProvider.missing_env?.length && (
                  <p className="text-red-600">
                    <span className="font-semibold">Missing:</span> {selectedOauthProvider.missing_env.join(', ')}
                  </p>
                )}
                {selectedOauthProvider.docs_url && (
                  <p>
                    <a
                      href={selectedOauthProvider.docs_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-700 underline"
                    >
                      Open {selectedOauthProvider.name} API docs
                    </a>
                  </p>
                )}
              </div>
            )}
            <div>
              <Label className="mb-2 block">OAuth Client ID (optional if server has default)</Label>
              <Input value={oauthClientId} onChange={(e) => setOauthClientId(e.target.value)} placeholder="Enter your broker app client id" />
            </div>
            <div>
              <Label className="mb-2 block">OAuth Client Secret (optional if server has default)</Label>
              <Input type="password" value={oauthClientSecret} onChange={(e) => setOauthClientSecret(e.target.value)} placeholder="Enter your broker app client secret" />
            </div>
            <Button
              onClick={openOAuthConnect}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
              disabled={loading || !provider || (selectedOauthProvider && !selectedOauthProvider.enabled)}
            >
              Connect with Broker Login
            </Button>
            <p className="text-xs text-slate-500">
              Uses secure OAuth redirect. No broker password is stored in StockKhata.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0 ring-1 ring-indigo-200/50">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <UploadCloud className="w-5 h-5 text-indigo-600" />
              API Token Connect
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="mb-2 block">Broker Provider</Label>
              <Select value={tokenProvider} onValueChange={setTokenProvider}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {providers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Access Token</Label>
              <Input type="password" value={accessToken} onChange={(e) => setAccessToken(e.target.value)} />
            </div>
            <div>
              <Label className="mb-2 block">Refresh Token (optional)</Label>
              <Input type="password" value={refreshToken} onChange={(e) => setRefreshToken(e.target.value)} />
            </div>
            <div>
              <Label className="mb-2 block">Broker Account ID (optional)</Label>
              <Input value={accountId} onChange={(e) => setAccountId(e.target.value)} />
            </div>
            {tokenProvider === 'custom' && (
              <>
                <div>
                  <Label className="mb-2 block">Custom Broker Name</Label>
                  <Input value={customProviderName} onChange={(e) => setCustomProviderName(e.target.value)} placeholder="My Broker" />
                </div>
                <div>
                  <Label className="mb-2 block">Trades API URL (optional but needed for sync)</Label>
                  <Input value={customTradesUrl} onChange={(e) => setCustomTradesUrl(e.target.value)} placeholder="https://broker.example.com/api/trades" />
                </div>
                <div>
                  <Label className="mb-2 block">Custom Headers JSON (optional)</Label>
                  <textarea
                    value={customHeadersJson}
                    onChange={(e) => setCustomHeadersJson(e.target.value)}
                    className="w-full rounded-md border border-slate-300 p-2 text-sm"
                    rows={3}
                    placeholder='{"X-API-KEY":"..."}'
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Custom Mapping JSON (optional)</Label>
                  <textarea
                    value={customMappingJson}
                    onChange={(e) => setCustomMappingJson(e.target.value)}
                    className="w-full rounded-md border border-slate-300 p-2 text-sm"
                    rows={5}
                    placeholder='{"trades_path":"data","trade_id":"trade_id","symbol":"symbol"}'
                  />
                </div>
              </>
            )}
            <Button
              onClick={connectToken}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
              disabled={loading || !tokenProvider || !accessToken}
            >
              Save Token Connection
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-xl border-0 ring-1 ring-slate-200">
        <CardHeader className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-900">Connected Brokers</CardTitle>
            <Button variant="outline" onClick={fetchProvidersAndConnections} className="rounded-xl">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block">From Date</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div>
              <Label className="mb-1 block">To Date</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-slate-600">Loading broker connections...</p>
          ) : connections.length === 0 ? (
            <p className="text-slate-600">No broker connected yet.</p>
          ) : (
            <div className="space-y-3">
              {connections.map((connection) => (
                <div
                  key={connection.id}
                  className="p-4 rounded-xl border border-slate-200 bg-white flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                >
                  <div>
                    <p className="font-semibold text-slate-900">{connection.provider_name || connection.provider}</p>
                    <p className="text-sm text-slate-600">
                      Status: {connection.status} | Last Sync: {connection.last_sync_at || 'Never'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => syncConnection(connection.id)}
                      disabled={syncingId === connection.id || connection.status !== 'connected'}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                    >
                      {syncingId === connection.id ? 'Syncing...' : 'Sync Trades'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => disconnectConnection(connection.id)}
                      className="border-red-300 text-red-600 hover:bg-red-50 rounded-xl"
                    >
                      <Unplug className="w-4 h-4 mr-2" />
                      Disconnect
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BrokerIntegrations;
